/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type ReactElement, createContext, forwardRef } from 'react';
import { type ContextValue, Provider, Row as RowCore, type RowProps as RowCoreProps, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { CellContext } from '../cell/cell.component';

interface RowProps<T> extends Omit<RowCoreProps<T>, 'style' | 'className'> {
  /** The priority of the row. Affects the color of text in cells. */
  variant?: 'primary' | 'inactive' | 'error' | 'new';
  style?: stylex.StyleXStyles;
}

const RowContext = createContext<ContextValue<RowProps<object>, HTMLDivElement>>({});

const Row = forwardRef((props: RowProps<object>, ref: ForwardedRef<HTMLDivElement>) => {
  const [mergedProps, mergedRef] = useContextProps(props, ref, RowContext);

  const { style, variant = 'primary', ...coreProps } = mergedProps;

  return (
    <Provider values={[
      [CellContext, {
        isLowPriority: variant === 'inactive',
      }]]}>
      <RowCore ref={mergedRef} {...stylex.props(stylex.defaultMarker(), styles.base, variantVariants[variant], style)} {...coreProps} />
    </Provider>
  );
}) as <T>(p: RowProps<T> & { ref?: ForwardedRef<HTMLTableRowElement> }) => ReactElement;

const styles = stylex.create({
  base: {
    outlineColor: {
      default: 'transparent',
      ':is([data-focus-visible])': $.borderInteractive,
    },
    outlineOffset: -2,
    outlineStyle: 'solid',
    outlineWidth: 2,
    boxSizing: 'border-box',
    cursor: 'default',
    position: 'relative',
    transitionDuration: '200ms',
    transitionProperty: 'background, color',
    borderBottomColor: $.border06,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    height: 36,
  },
});

const newRowHighlight = stylex.keyframes({
  from: { backgroundColor: $.surfaceAttentiveSilent },
  to: { backgroundColor: $.surfaceBGPrimary },
});

const variantVariants = stylex.create({
  primary: {
    backgroundColor: $.surfaceBGPrimary,
    backgroundImage: {
      default: null,
      ':focus-visible': 'linear-gradient(0deg, rgba(0, 0, 0, 0.023) 0%, rgba(0, 0, 0, 0.023) 100%)',
      ':is([data-row-hovered="true"])': 'linear-gradient(0deg, rgba(0, 0, 0, 0.023) 0%, rgba(0, 0, 0, 0.023) 100%)',
      ':hover': 'linear-gradient(0deg, rgba(0, 0, 0, 0.023) 0%, rgba(0, 0, 0, 0.023) 100%)',
    },
  },
  inactive: {
    backgroundColor: $.surfaceBGPrimary,
    backgroundImage: {
      default: null,
      ':focus-visible': 'linear-gradient(0deg, rgba(0, 0, 0, 0.023) 0%, rgba(0, 0, 0, 0.023) 100%)',
      ':is([data-row-hovered="true"])': 'linear-gradient(0deg, rgba(0, 0, 0, 0.023) 0%, rgba(0, 0, 0, 0.023) 100%)',
      ':hover': 'linear-gradient(0deg, rgba(0, 0, 0, 0.023) 0%, rgba(0, 0, 0, 0.023) 100%)',
    },
  },
  error: {
    backgroundColor: $.surfaceCriticalSilent,
    backgroundImage: {
      default: 'linear-gradient(0deg, rgba(0, 0, 0, 0.01) 0%, rgba(0, 0, 0, 0.01) 100%)',
      ':focus-visible': 'linear-gradient(0deg, rgba(0, 0, 0, 0.033) 0%, rgba(0, 0, 0, 0.033) 100%)',
      ':is([data-row-hovered="true"])': 'linear-gradient(0deg, rgba(0, 0, 0, 0.033) 0%, rgba(0, 0, 0, 0.033) 100%)',
      ':hover': 'linear-gradient(0deg, rgba(0, 0, 0, 0.033) 0%, rgba(0, 0, 0, 0.033) 100%)',
    },
  },
  new: {
    animationName: newRowHighlight,
    animationDuration: '10s',
    animationTimingFunction: 'ease-in',
    animationFillMode: 'forwards',
    backgroundImage: {
      default: 'linear-gradient(0deg, rgba(0, 0, 0, 0.01) 0%, rgba(0, 0, 0, 0.01) 100%)',
      ':focus-visible': 'linear-gradient(0deg, rgba(0, 0, 0, 0.033) 0%, rgba(0, 0, 0, 0.033) 100%)',
      ':is([data-row-hovered="true"])': 'linear-gradient(0deg, rgba(0, 0, 0, 0.033) 0%, rgba(0, 0, 0, 0.033) 100%)',
      ':hover': 'linear-gradient(0deg, rgba(0, 0, 0, 0.033) 0%, rgba(0, 0, 0, 0.033) 100%)',
    },
  },
});

export { Row, RowContext, type RowProps };
